/**
 * SIL-1349 - the node Passport drawer (spec section 4.3, handoff section 3).
 *
 * A 540px right sheet over a `black/50` scrim, opened from a row click or the
 * "Passport" button in `LotContentsTable`. It is a SUMMARY with a link out, not
 * a second passport page.
 *
 * ## One route in, and only one
 *
 * Every section below renders from `GET /api/client/listings/:id/members/
 * :assetId/passport-summary` and nothing else. That endpoint (SIL-1345) already
 * decided access - verified buyer or listing owner - and already decided which
 * sections exist: **a section with no source data is OMITTED from the response,
 * never nulled**, so this component's job is to hide the section it has no key
 * for, not to null-check every field. Reading the node's identity or verdict off
 * `lotMembers` instead would give the drawer a second, independently-derived
 * view of the same node, and the two disagreeing is the bug.
 *
 * The only value that crosses over from the table is the `assetId` - the key for
 * the request.
 *
 * ## The Verified mark is not this file's decision
 *
 * `showsVerifiedMark` (AvStateChip.tsx) is the single implementation of the rule
 * that Tier 3, a failed window verdict (SIL-1371) and both neutral AV states
 * withhold the mark, and `AvStateChip` applies it. This file does not re-derive
 * it. What it DOES derive - from the node's own synthesized run, because the
 * summary payload carries no `avState` of its own - is the three-valued state to
 * hand that chip, in `nodeAvState` below, plus the verdict family in
 * `nodeVerdictId`, which now feeds the chip and the verdict badge from one call.
 *
 * ## Sections are reused, not re-implemented
 *
 * `ActiveVerificationSection` and `ProvenanceChainSection` are imported from the
 * shared `AssetPassportBody` (exported there for exactly this). The full §1-§5
 * body is deliberately NOT rendered: it is a print document with confidence
 * gauges, health scores, damage budgets and a disclaimer, none of which this
 * drawer's six sections include.
 */
import { X } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { PassportVerification } from '@siliconx/shared';
import type {
  ListingMemberPassportSummary,
  PassportSummaryGpuSlot,
} from '@siliconx/shared/dto/listing-passport-summary';
import type { AvState } from '@siliconx/shared/dto/marketplace';
import {
  ActiveVerificationSection,
  ProvenanceChainSection,
} from '@siliconx/shared/ui/AssetPassportBody';
import { SiliconXMark } from '@siliconx/shared/passport/logo';
import { Button } from '@siliconx/shared/ui/Button';
import { cn } from '@siliconx/shared/ui/utils';
import { verificationVerdictFamily } from '@siliconx/shared/verification/presentation';
import { api, throwIfNotOk } from '../../lib/api';
import { qk } from '../../lib/queryKeys';
import { usePassportArtifactDownload } from '../passport/PassportSurface';
import { ActiveVerificationExplainerTrigger } from '@siliconx/shared/ui/buyer-listing/ActiveVerificationExplainerModal';
import { AvStateChip, AvStateNote } from '@siliconx/shared/ui/buyer-listing/AvStateChip';
import { VerdictBadge } from '@siliconx/shared/ui/buyer-listing/LotContentsTable';
import { exchangeErrorMessage } from './errors';
import {
  NODE_DRAWER_ARIA_LABEL,
  NODE_DRAWER_ATTESTATION_LABELS,
  NODE_DRAWER_AV_HEADING,
  NODE_DRAWER_CLOSE_LABEL,
  NODE_DRAWER_CUSTODY_HEADING,
  NODE_DRAWER_DOWNLOAD_REPORT,
  NODE_DRAWER_ERROR_FALLBACK,
  NODE_DRAWER_EYEBROW,
  NODE_DRAWER_GPUS_HEADING,
  NODE_DRAWER_GPU_COLUMNS,
  NODE_DRAWER_GPU_MEASURED,
  NODE_DRAWER_GPU_NO_SERIAL,
  NODE_DRAWER_LOADING,
  NODE_DRAWER_OPEN_FULL_PASSPORT,
  nodeDrawerGpuSlotLabel,
} from './copy/nodePassportDrawer';

// ---------------------------------------------------------------------------
// Derivations from the node's own synthesized run
// ---------------------------------------------------------------------------

/**
 * The three-valued AV state for THIS node.
 *
 * `passport-summary` carries no `avState` field: unlike the listing-level
 * payload, which rolls many nodes up into one state, a single node's state is
 * fully determined by its own synthesized run, so the server sends the run and
 * nothing redundant. This is the derivation, in one place, and it is NOT a
 * second copy of the Verified-mark rule - that stays in `showsVerifiedMark`,
 * which consumes this value.
 *
 *  - no run at all (the key is absent)      -> 'none'
 *  - a pass that has aged out of its window -> 'expired'
 *  - anything else                          -> 'active'
 *
 * `'expired'` and `'none'` are both the NEUTRAL third state and neither is a
 * failure. A failed or flagged run is `'active'`: the run is current, its
 * verdict is simply adverse, and collapsing the two would be exactly the
 * conflation the three-valued state was introduced to prevent.
 */
export function nodeAvState(verification: PassportVerification | undefined): AvState {
  if (verification === undefined || verification.kind === 'none') return 'none';
  if (verification.kind === 'tier12_pass' && verification.stale) return 'expired';
  return 'active';
}

/** The worst-in-window evidence tier, or null when there is no run to have one. */
export function nodeEvidenceTier(verification: PassportVerification | undefined): 1 | 2 | 3 | null {
  if (verification === undefined || verification.kind === 'none') return null;
  if (verification.kind === 'tier3_self_reported') return 3;
  return verification.tier;
}

/**
 * The synthesized run mapped onto the three REVIEWED verdicts in
 * `ACTIVE_VERIFICATION_VERDICTS`, so the drawer's badge reuses that copy rather
 * than introducing a fourth label.
 *
 * This function's job is the `kind` → family mapping and nothing else. Wherever
 * a branch holds a RAW classification (`fail:thermal_throttle`,
 * `flag:serial_mismatch`, `invalid_run`) the raw → family step is delegated to
 * `verificationVerdictFamily` in `@siliconx/shared/verification/presentation`,
 * which is the ONE place that vocabulary lives (SIL-1366). Restating it here
 * would be a second copy of a table that is expected to grow.
 *
 * A `flag:` verdict is `pass_with_observations`, never `fail` — "no failures,
 * with a signal worth disclosing" is what the reviewed copy for it says, and
 * rendering it in the failure palette would overstate it. That now holds on
 * Tier 3 as well: `passed` is false for a flag, so this branch used to badge a
 * flagged Tier-3 run as a Fail while `tier12_flagged` called the same
 * classification an observation. `invalid_run` likewise now reads as the
 * neutral state instead of a Pass — `passed` is true for it, since it is
 * neither a `fail:` nor a `flag:`, and a run that could not complete is not a
 * pass (SIL-1366).
 */
export function nodeVerdictId(
  verification: PassportVerification | undefined,
): 'pass' | 'pass_with_observations' | 'fail' | null {
  if (verification === undefined) return null;
  switch (verification.kind) {
    case 'none':
      return null;
    case 'tier12_pass':
      // Already a family: the variant types this field as one of the two pass ids.
      return verification.verdict;
    case 'tier12_failed':
    case 'tier12_flagged':
      return verificationVerdictFamily(verification.classification);
    case 'tier3_self_reported':
      return verificationVerdictFamily(verification.verdict);
  }
}

// ---------------------------------------------------------------------------
// Data
// ---------------------------------------------------------------------------

interface ApiError extends Error {
  status?: number;
}

/**
 * The drawer's ONLY request. A 404 is terminal and must not be retried: the
 * endpoint answers 404 (never 403) both for a listing this caller cannot see and
 * for an `assetId` that is not a member of it, because a 403 there would confirm
 * the asset exists across orgs.
 */
export function useNodePassportSummary(listingId: string, assetId: string) {
  return useQuery<ListingMemberPassportSummary, ApiError>({
    queryKey: qk.listings.nodePassportSummary(listingId, assetId),
    queryFn: async () => {
      const res = await api.api.client.listings[':id'].members[':assetId']['passport-summary'].$get({
        param: { id: listingId, assetId },
      });
      if (!res.ok) {
        await throwIfNotOk(res);
        throw Object.assign(new Error(`HTTP ${res.status}`), { status: res.status });
      }
      return (await res.json()) as unknown as ListingMemberPassportSummary;
    },
    retry: (failCount, err) => err.status !== 404 && err.status !== 403 && failCount < 2,
  });
}

// ---------------------------------------------------------------------------
// Sections
// ---------------------------------------------------------------------------

function SectionHeading({ children }: { children: string }) {
  return <h3 className="text-sm font-semibold text-foreground">{children}</h3>;
}

const ATTESTATION_TONE: Record<PassportSummaryGpuSlot['attestation'], string> = {
  attested_match: 'text-emerald-700',
  attested_mismatch: 'text-red-700',
  // Never a failure tone: Tier 2/3, and a Tier 1 part exposing no serial, are
  // correctly "not attested" and nothing is wrong.
  not_attested: 'text-muted-foreground',
};

function GpuSlotTable({ gpus }: { gpus: PassportSummaryGpuSlot[] }) {
  return (
    <table className="mt-2 w-full text-sm">
      <thead>
        <tr className="text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          <th scope="col" className="py-1 pr-2 font-semibold">{NODE_DRAWER_GPU_COLUMNS.slot}</th>
          <th scope="col" className="py-1 pr-2 font-semibold">{NODE_DRAWER_GPU_COLUMNS.serial}</th>
          <th scope="col" className="py-1 font-semibold">{NODE_DRAWER_GPU_COLUMNS.attestation}</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-border">
        {gpus.map((gpu) => (
          <tr key={gpu.slot}>
            <td className="py-1.5 pr-2 align-top text-foreground">
              {nodeDrawerGpuSlotLabel(gpu.slot)}
              <span className="ml-1 text-xs text-muted-foreground">
                {[gpu.vendor, gpu.name].filter(Boolean).join(' ')}
              </span>
              {/* SIL-688: false for EVERY slot when the harness could not
                  disambiguate, so this never claims a measured device it was
                  not told about. */}
              {gpu.measured && (
                <span className="ml-1 text-xs text-muted-foreground">({NODE_DRAWER_GPU_MEASURED})</span>
              )}
            </td>
            <td className="py-1.5 pr-2 align-top font-mono text-xs text-foreground">
              {gpu.serial ?? (
                <span className="font-sans text-muted-foreground">{NODE_DRAWER_GPU_NO_SERIAL}</span>
              )}
            </td>
            <td className={cn('py-1.5 align-top text-xs font-medium', ATTESTATION_TONE[gpu.attestation])}>
              {NODE_DRAWER_ATTESTATION_LABELS[gpu.attestation]}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function DrawerBody({ summary, listingId }: { summary: ListingMemberPassportSummary; listingId: string }) {
  const verification = summary.activeVerification;
  const avState = nodeAvState(verification);
  const evidenceTier = nodeEvidenceTier(verification);
  // SIL-1371 — ONE derivation feeding both badges. The chip gates the Verified
  // mark on this and the row badge renders it, so the two can no longer disagree:
  // before, a `fail:thermal_throttle` node showed the green Verified chip beside
  // its own red Fail badge.
  const verdictId = nodeVerdictId(verification);
  const title =
    [summary.manufacturerName, summary.skuModelName].filter(Boolean).join(' ') || summary.fingerprint;

  return (
    <>
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            <SiliconXMark size={16} />
            {NODE_DRAWER_EYEBROW}
            <span className="font-mono tracking-normal">· {summary.fingerprint}</span>
          </p>
          <h2 className="mt-1 truncate text-[17px] font-semibold text-foreground">{title}</h2>
          {summary.chassisSerial !== null && (
            <p className="mt-0.5 font-mono text-xs text-muted-foreground">{summary.chassisSerial}</p>
          )}
        </div>
      </div>

      {/* Badges — the AV state chip owns the Verified-mark decision; the verdict
          badge is the same component the lot row uses. */}
      <div className="mt-4">
        <div className="flex flex-wrap items-center gap-2" data-testid="node-passport-badges">
          <AvStateChip avState={avState} evidenceTier={evidenceTier} windowVerdict={verdictId} />
          <VerdictBadge verdict={verdictId} />
        </div>
        <AvStateNote avState={avState} className="mt-2" />
      </div>

      {/* Active Verification run — absent from the response means absent here. */}
      {verification !== undefined && (
        <section className="mt-6">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <SectionHeading>{NODE_DRAWER_AV_HEADING}</SectionHeading>
            <ActiveVerificationExplainerTrigger />
          </div>
          <div className="mt-2">
            <ActiveVerificationSection verification={verification} />
          </div>
        </section>
      )}

      {summary.gpus !== undefined && (
        <section className="mt-6">
          <SectionHeading>{NODE_DRAWER_GPUS_HEADING}</SectionHeading>
          <GpuSlotTable gpus={summary.gpus} />
        </section>
      )}

      {summary.custody !== undefined && (
        <section className="mt-6">
          <SectionHeading>{NODE_DRAWER_CUSTODY_HEADING}</SectionHeading>
          <div className="mt-2">
            <ProvenanceChainSection provenance={summary.custody} verification={verification} />
          </div>
        </section>
      )}

      <DrawerFooter fingerprint={summary.fingerprint} listingId={listingId} />
    </>
  );
}

function DrawerFooter({ fingerprint, listingId }: { fingerprint: string; listingId: string }) {
  const { artifact, download, isPending } = usePassportArtifactDownload(fingerprint);

  return (
    <div className="mt-8 flex flex-wrap items-center gap-2 border-t border-border pt-4">
      {/* Renders nothing when this caller may see no stamped artifact, rather
          than offering a download the report route would 404. */}
      {artifact !== null && (
        <Button variant="secondary" size="sm" onClick={download} disabled={isPending}>
          {NODE_DRAWER_DOWNLOAD_REPORT}
        </Button>
      )}
      <Link
        to="/passport/$fingerprint"
        params={{ fingerprint }}
        search={{ listing: listingId }}
        className="inline-flex items-center rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700"
      >
        {NODE_DRAWER_OPEN_FULL_PASSPORT}
      </Link>
    </div>
  );
}

// ---------------------------------------------------------------------------

export interface NodePassportDrawerProps {
  listingId: string;
  /** The lot member's asset id — the ONLY value that crosses over from the table. */
  assetId: string;
  onClose: () => void;
}

export function NodePassportDrawer({ listingId, assetId, onClose }: NodePassportDrawerProps) {
  const { data: summary, isLoading, error } = useNodePassportSummary(listingId, assetId);

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/50" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={NODE_DRAWER_ARIA_LABEL}
        className="h-full w-full max-w-[540px] overflow-y-auto bg-card p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label={NODE_DRAWER_CLOSE_LABEL}
          className="float-right rounded-md p-1 text-muted-foreground hover:bg-muted"
        >
          <X className="size-4" />
        </button>

        {isLoading ? (
          <p className="text-sm text-muted-foreground">{NODE_DRAWER_LOADING}</p>
        ) : error || summary === undefined ? (
          <p className="text-sm text-muted-foreground">
            {error
              ? exchangeErrorMessage(error, NODE_DRAWER_ERROR_FALLBACK)
              : NODE_DRAWER_ERROR_FALLBACK}
          </p>
        ) : (
          <DrawerBody summary={summary} listingId={listingId} />
        )}
      </div>
    </div>
  );
}
